import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Theme modes:
 *   - "system" → follow OS preference (when enabled)
 *   - "light"  → force light
 *   - "dark"   → force dark
 *
 * iter-86: per the user's "remain the ui only dark for now" directive,
 * the resolver is HARD-PINNED to dark. The mode-picker UI on /appearance
 * still records the user's intended choice (so we can lift the pin later
 * without losing their selection) but the DOM always renders dark.
 * Flip DARK_ONLY back to false to restore the System/Light/Dark behavior.
 */
const DARK_ONLY = true;
const ThemeContext = createContext({
    mode: "system",
    theme: "dark",
    setMode: () => {},
    toggle: () => {},
});

const KEY = "moodcard.theme";

const validModes = new Set(["system", "light", "dark"]);

function getSystemTheme() {
    if (typeof window === "undefined" || !window.matchMedia) return "dark";
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function ThemeProvider({ children }) {
    // `mode` is what the user picked. `theme` is the resolved value applied to <html>.
    const [mode, setModeState] = useState("system");
    const [theme, setTheme] = useState("dark");

    // Hydrate persisted mode on mount.
    useEffect(() => {
        let initial = "system";
        try {
            const stored = localStorage.getItem(KEY);
            if (stored && validModes.has(stored)) initial = stored;
            // legacy: old key persisted "light"/"dark" directly — keep honoring that.
        } catch { /* ignore */ }
        setModeState(initial);
    }, []);

    // Resolve mode → theme, plus subscribe to system changes when in "system" mode.
    useEffect(() => {
        // iter-86 dark-only override: regardless of stored mode, we always
        // render dark while DARK_ONLY is true. Stored mode is preserved.
        if (DARK_ONLY) { setTheme("dark"); return undefined; }
        const resolve = () => {
            if (mode === "system") setTheme(getSystemTheme());
            else setTheme(mode);
        };
        resolve();
        if (mode !== "system") return undefined;
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        const mql = window.matchMedia("(prefers-color-scheme: light)");
        const onChange = () => resolve();
        try { mql.addEventListener("change", onChange); }
        catch { mql.addListener(onChange); }
        return () => {
            try { mql.removeEventListener("change", onChange); }
            catch { mql.removeListener(onChange); }
        };
    }, [mode]);

    // Apply resolved theme to <html> + persist user's chosen MODE.
    useEffect(() => {
        const root = document.documentElement;
        if (theme === "light") root.classList.add("light");
        else root.classList.remove("light");
    }, [theme]);

    useEffect(() => {
        try { localStorage.setItem(KEY, mode); } catch { /* ignore */ }
    }, [mode]);

    const setMode = useCallback((m) => {
        setModeState(validModes.has(m) ? m : "system");
    }, []);

    // Toggle cycles: dark → light → system → dark
    const toggle = useCallback(() => {
        setModeState((m) => (m === "dark" ? "light" : m === "light" ? "system" : "dark"));
    }, []);

    // Back-compat: previous code called `setTheme("dark"|"light")` directly.
    const legacySetTheme = useCallback((t) => {
        setModeState(t === "light" ? "light" : "dark");
    }, []);

    return (
        <ThemeContext.Provider value={{ mode, theme, setMode, setTheme: legacySetTheme, toggle }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);
